import React from 'react';
import { Button } from '@rocco/components/button';
import { cn } from '@rocco/utils/cn';
import type { FormFooterProps } from './types';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';
import { ReactComponent as ArrowLeft } from '@rocco/icons/svg/arrow/arrow-left.svg';

/**
 * Form footer component with Back, Next and Skip buttons
 * Handles responsive layout and accessibility
 */
export function FormFooter({
    isNextDisabled = false,
    hideNextButton = false,
    showBackButton,
    showSkipButton,
    showBackInsteadOfCancel = false,
    hideCancelButton = false,
    nextButtonText,
    backButtonText,
    cancelButtonText = 'Cancel',
    skipButtonText,
    secondaryActionText,
    showSecondaryAction = false,
    isFirstStep,
    isLastStep,
    onNext,
    onBack,
    onCancel,
    onSkip,
    onSecondaryAction,
    className,
    variant = 'default',
    nextButtonClassName,
}: FormFooterProps) {
    const shouldShowSkipButton = isNextDisabled && showSkipButton;
    const shouldDisableNextButton = isNextDisabled;
    const hasSecondaryAction = Boolean(
        secondaryActionText && onSecondaryAction,
    );

    const hideFooter = false;

    if (hideFooter) {
        return null;
    }

    // HDE variant: light mode (outlined Back/Cancel, dark filled Continue)
    if (variant === 'hde') {
        const pillButtonBase =
            'flex h-[2.835rem] min-w-0 flex-1 items-center justify-center rounded-full text-[0.986rem] font-medium leading-[1.3] tracking-[0.02rem] md:flex-none md:w-[8.32rem]';

        return (
            <div
                className={cn(
                    'flex flex-col items-center justify-center gap-3 px-4 md:gap-4 md:px-12',
                    className,
                )}
            >
                <div className="flex w-full justify-center gap-4 md:w-auto md:gap-5">
                    {isFirstStep && !showBackInsteadOfCancel ? (
                        <>
                            {!hideCancelButton && (
                                <Button
                                    onClick={onCancel}
                                    size="larger"
                                    noHover
                                    className={cn(
                                        pillButtonBase,
                                        'border border-black bg-transparent text-[#1c1d1f]',
                                    )}
                                    aria-label="Cancel and close form"
                                >
                                    {cancelButtonText}
                                </Button>
                            )}
                            {!hideNextButton && (
                                <Button
                                    onClick={onNext}
                                    size="larger"
                                    disabled={shouldDisableNextButton}
                                    noHover
                                    className={cn(
                                        pillButtonBase,
                                        'border-0 bg-[#38383B] text-white shadow-[0_0.269rem_1.615rem_0_rgba(0,0,0,0.34)]',
                                        nextButtonClassName,
                                        shouldDisableNextButton &&
                                            'cursor-not-allowed opacity-50',
                                    )}
                                    aria-label={
                                        isLastStep
                                            ? 'Complete form'
                                            : 'Go to next step'
                                    }
                                >
                                    {nextButtonText}
                                </Button>
                            )}
                        </>
                    ) : showBackButton ? (
                        <>
                            <Button
                                onClick={onBack}
                                size="larger"
                                noHover
                                className={cn(
                                    pillButtonBase,
                                    'border border-black bg-transparent text-[#1c1d1f]',
                                )}
                                aria-label="Go to previous step"
                            >
                                {backButtonText}
                            </Button>
                            {!hideNextButton && (
                                <Button
                                    onClick={onNext}
                                    size="larger"
                                    disabled={shouldDisableNextButton}
                                    noHover
                                    className={cn(
                                        pillButtonBase,
                                        'border-0 bg-[#38383B] text-white shadow-[0_0.269rem_1.615rem_0_rgba(0,0,0,0.34)]',
                                        nextButtonClassName,
                                        shouldDisableNextButton &&
                                            'cursor-not-allowed opacity-50',
                                    )}
                                    aria-label={
                                        isLastStep
                                            ? 'Complete form'
                                            : 'Go to next step'
                                    }
                                >
                                    {nextButtonText}
                                </Button>
                            )}
                        </>
                    ) : (
                        !hideNextButton && (
                            <Button
                                onClick={onNext}
                                size="larger"
                                disabled={shouldDisableNextButton}
                                noHover
                                className={cn(
                                    pillButtonBase,
                                    'border-0 bg-[#38383B] text-white',
                                    nextButtonClassName,
                                    shouldDisableNextButton &&
                                        'cursor-not-allowed opacity-50',
                                )}
                                aria-label={
                                    isLastStep
                                        ? 'Complete form'
                                        : 'Go to next step'
                                }
                            >
                                {nextButtonText}
                            </Button>
                        )
                    )}
                </div>
                {(shouldShowSkipButton || hasSecondaryAction) && (
                    <div className="min-h-5">
                        {shouldShowSkipButton ? (
                            <button
                                type="button"
                                onClick={onSkip}
                                className="mx-auto text-gray-500 underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none"
                                aria-label="Skip this step"
                            >
                                {skipButtonText}
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={onSecondaryAction}
                                className={cn(
                                    'mx-auto text-gray-500 underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none',
                                    'transition-opacity duration-500',
                                    showSecondaryAction
                                        ? 'opacity-100'
                                        : 'pointer-events-none opacity-0',
                                )}
                                aria-label={secondaryActionText}
                            >
                                {secondaryActionText}
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // Default variant: original behavior with arrows on both buttons
    return (
        <div
            className={cn(
                'flex flex-col gap-4 py-6',
                'md:items-center md:justify-between',
                className,
            )}
        >
            <div className="flex gap-4 md:flex-row md:items-center md:justify-between">
                <div className="order-1 flex md:order-1">
                    {showBackButton ? (
                        <Button
                            onClick={onBack}
                            color="outline"
                            size="larger"
                            className={cn(
                                'w-full border-transparent bg-[#DBDBDB] md:w-auto md:min-w-34',
                            )}
                            aria-label="Go to previous step"
                        >
                            <ArrowLeft className="mr-2.5 h-4.5 w-4.5" />
                            {backButtonText}
                        </Button>
                    ) : (
                        <div />
                    )}
                </div>

                <div className="order-2 flex gap-3">
                    {!hideNextButton && (
                        <Button
                            onClick={onNext}
                            color="primary"
                            size="larger"
                            disabled={shouldDisableNextButton}
                            className={cn(
                                'w-full transition-all duration-300 md:w-auto md:min-w-34',
                                shouldDisableNextButton &&
                                    'cursor-not-allowed opacity-50',
                            )}
                            aria-label={
                                isLastStep ? 'Complete form' : 'Go to next step'
                            }
                        >
                            {nextButtonText}
                            <ArrowRight className="ml-2.5 h-4.5 w-4.5" />
                        </Button>
                    )}
                </div>
            </div>

            <div
                className={cn(
                    'flex min-h-8 justify-center',
                    shouldShowSkipButton && 'order-3',
                )}
            >
                {shouldShowSkipButton && (
                    <div
                        onClick={onSkip}
                        className={cn(
                            'w-full opacity-50 md:w-auto md:min-w-34',
                        )}
                        role="button"
                        aria-label="Skip this step"
                    >
                        {skipButtonText}
                    </div>
                )}
                {!shouldShowSkipButton && hasSecondaryAction && (
                    <button
                        type="button"
                        onClick={onSecondaryAction}
                        className={cn(
                            'w-full text-center text-xs text-[#2A2A2A]/55 transition-opacity duration-500 md:w-auto md:min-w-34',
                            showSecondaryAction
                                ? 'opacity-100'
                                : 'pointer-events-none opacity-0',
                        )}
                        aria-label={secondaryActionText}
                    >
                        {secondaryActionText}
                    </button>
                )}
            </div>
        </div>
    );
}
